// The inspector beside a note: where it links, which notes link to it, its tags. Also the list of
// notes that mention an entity, shown with the entity's details.
import { Bot, ChevronRight, FilePlus2, FileText } from 'lucide-react';
import { openCopyForAgent } from '../agent/CopyForAgent';
import { useMemo, useState } from 'react';
import { cn } from '../lib/cn';
import { openNote } from '../state/dock';
import { useWorkspace } from '../state/workspace';
import { KindIcon } from './LinkPreview';
import { backlinks, parseLinks, parseTags, resolveLink, targetKey, targetLabel } from '@modelviewer/core';
import { createLinkedNote, openTarget } from './navigate';
import { useLinkIndex } from './useLinkIndex';

function Section({ title, count, children }: { title: string; count?: number; children: React.ReactNode }) {
  const [open, setOpen] = useState(true);
  return (
    <section className="border-b border-border">
      <button onClick={() => setOpen(!open)} className="flex h-8 w-full items-center gap-1 px-3 text-[11px] font-semibold tracking-wider text-faint uppercase hover:text-muted" aria-expanded={open}>
        <ChevronRight size={12} className={cn('transition-transform', open && 'rotate-90')} />
        {title}
        {count !== undefined && <span className="ml-auto font-normal tracking-normal normal-case">{count}</span>}
      </button>
      {open && <div className="px-1.5 pb-3">{children}</div>}
    </section>
  );
}

const rowClass = 'flex w-full items-center gap-2 rounded-md px-1.5 py-1 text-left text-[12.5px] hover:bg-hover';

/** Notes that link to something (a targetKey). */
export function MentionedIn({ target }: { target: string }) {
  const notes = useWorkspace((s) => s.notes);
  const index = useLinkIndex();
  const list = useMemo(() => backlinks(Object.values(notes), index, target), [notes, index, target]);
  if (list.length === 0) return <p className="px-1.5 text-[12px] text-faint">No notes mention it yet. Link to it from a note with [[its name]].</p>;
  return (
    <div data-testid="mentioned-in">
      {list.map(({ note, count }) => (
        <button key={note.id} onClick={() => openNote(note.id)} className={rowClass}>
          <FileText size={13} className="flex-none text-faint" />
          <span className="min-w-0 flex-1 truncate">{note.title}</span>
          {count > 1 && <span className="text-[11px] text-faint">{count}×</span>}
        </button>
      ))}
    </div>
  );
}

export function NoteInspector({ noteId }: { noteId: string }) {
  const note = useWorkspace((s) => s.notes[noteId]);
  const notes = useWorkspace((s) => s.notes);
  const index = useLinkIndex();
  const outgoing = useMemo(() => {
    const seen = new Set<string>();
    const out: { target: string; resolved: ReturnType<typeof resolveLink> }[] = [];
    for (const l of parseLinks(note?.body ?? '')) {
      const resolved = resolveLink(l.target, index);
      const key = resolved ? targetKey(resolved) : `?${l.target.toLowerCase()}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({ target: l.target, resolved });
    }
    return out;
  }, [note?.body, index]);
  const incoming = useMemo(() => backlinks(Object.values(notes), index, `note:${noteId}`), [notes, index, noteId]);
  if (!note) return null;
  const tags = parseTags(note.body);
  return (
    <aside className="h-full overflow-y-auto" aria-label="Inspector">
      <header className="border-b border-border px-3 py-3">
        <p className="mb-1 text-[11px] font-semibold tracking-wider text-faint uppercase">Note</p>
        <h2 className="truncate text-[14px] font-semibold">{note.title}</h2>
        <p className="mt-0.5 text-[12px] text-muted">Edited {new Date(note.updated).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}</p>
        <button
          onClick={() => openCopyForAgent({ noteId, base: useWorkspace.getState().activeRef })}
          className="mt-2 inline-flex h-7 items-center gap-1.5 rounded-md border border-border px-2.5 text-[12.5px] hover:bg-hover"
          title="A prompt for an agent to design for this use case"
        >
          <Bot size={13} /> Copy for agent
        </button>
      </header>
      <Section title="Links" count={outgoing.length}>
        {outgoing.length === 0 && <p className="px-1.5 text-[12px] text-faint">Type [[ in the note to link to an entity, a sketch, a migration or a note.</p>}
        <div data-testid="note-links">
          {outgoing.map(({ target, resolved }) =>
            resolved ? (
              <button key={targetKey(resolved)} onClick={() => openTarget(resolved)} className={rowClass}>
                <span className="flex-none text-faint">
                  <KindIcon kind={resolved.kind} />
                </span>
                <span className="min-w-0 flex-1 truncate">{targetLabel(resolved)}</span>
                <span className="flex-none text-[11px] text-faint">{resolved.kind === 'entity' ? resolved.model : resolved.kind}</span>
              </button>
            ) : (
              <button key={target} onClick={() => void createLinkedNote(target)} className={cn(rowClass, 'text-muted')} title="Create a note with this name">
                <FilePlus2 size={13} className="flex-none text-faint" />
                <span className="min-w-0 flex-1 truncate border-b border-dashed border-faint">{target}</span>
                <span className="flex-none text-[11px] text-faint">not found</span>
              </button>
            ),
          )}
        </div>
      </Section>
      <Section title="Backlinks" count={incoming.length}>
        {incoming.length === 0 && <p className="px-1.5 text-[12px] text-faint">No other note links here.</p>}
        {incoming.map(({ note: n }) => (
          <button key={n.id} onClick={() => openNote(n.id)} className={rowClass}>
            <FileText size={13} className="flex-none text-faint" />
            <span className="min-w-0 flex-1 truncate">{n.title}</span>
          </button>
        ))}
      </Section>
      {tags.length > 0 && (
        <Section title="Tags" count={tags.length}>
          <div className="flex flex-wrap gap-1.5 px-1.5">
            {tags.map((t) => (
              <span key={t} className="mv-note-tag">
                #{t}
              </span>
            ))}
          </div>
        </Section>
      )}
    </aside>
  );
}

